import React from "react";
import { PageHeader, Card } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable } from "../hooks/useAirtable";
import { num, fmtDate, indexById, linkedName } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import PeriodScopeBar, { usePeriodFilter } from "../components/ui-bj/PeriodScopeBar";

const avg = (...vals) => {
  const nums = vals.map((v) => num(v)).filter((v) => v !== null);
  if (!nums.length) return null;
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
};

const CreditPipelinePage = () => {
  const credit = useAirtable("credit");
  const clients = useAirtable("clients");
  const clientMap = React.useMemo(() => indexById(clients.records), [clients.records]);
  const { period, setPeriod, scope, setScope, filterByDate } = usePeriodFilter("all");

  if (credit.loading || clients.loading) return <Loading label="Loading pipeline" />;
  if (credit.error) return <ErrorState message={credit.error} onRetry={credit.refetch} />;

  const rows = credit.records
    .map((r) => {
      const f = r.fields || {};
      const start = avg(f["Starting Score EQ"], f["Starting Score EX"], f["Starting Score TU"]);
      const cur = avg(f["Current Score EQ"], f["Current Score EX"], f["Current Score TU"]);
      const gain = num(f["Score Gain"]);
      return {
        id: r.id,
        client: linkedName(f["Client Link"], clientMap),
        startScore: start,
        currentScore: cur,
        gain,
        round: num(f["Dispute Round"]),
        removed: num(f["Items Removed"]),
        lastUpdated: f["Last Updated"] || f["Last Dispute Date"],
      };
    })
    .sort((a, b) => (b.gain ?? -999) - (a.gain ?? -999));

  // Period filter scopes Financial Profile Development to the "Last Updated" date.
  const visibleRows = rows.filter((r) => filterByDate(r.lastUpdated));

  return (
    <div data-testid="credit-pipeline-page">
      <PageHeader
        eyebrow="DASHBOARD / FINANCIAL PROFILE DEVELOPMENT"
        title="Financial Profile Development"
        subtitle={`${visibleRows.length} of ${rows.length} active engagements • ${scope === "period" ? "filtered by Last Updated" : "all-time view"} • Sorted by score gain (descending).`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodScopeBar scope={scope} setScope={setScope} period={period} setPeriod={setPeriod} testidPrefix="credit-scope" />
            <ExportButtons
              title="Financial Profile Development"
              filename="financial-profile-development"
              testidPrefix="export-credit"
              columns={[
                { key: "client", label: "Client" },
                { key: "startScore", label: "Start Score" },
                { key: "currentScore", label: "Current Score" },
                { key: "gain", label: "Score Gain" },
                { key: "round", label: "Review Round" },
                { key: "removed", label: "Items Corrected" },
                { key: "lastUpdated", label: "Last Updated" },
              ]}
              rows={visibleRows}
            />
          </div>
        }
      />
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="text-left">
              {["Client","Start Score","Current Score","Score Gain","Review Round","Items Corrected","Last Updated"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-[#5E4D40] text-sm">No financial profile records for this filter.</td></tr>}
            {visibleRows.map((row, i) => (
              <tr key={row.id} data-testid={`credit-row-${i}`} className="bj-table-row">
                <td className="py-3 pr-3 text-[#2A1C15] text-sm">{row.client}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm bj-tnum">{row.startScore ?? "—"}</td>
                <td className="py-3 pr-3 text-[#2A1C15] text-sm bj-tnum">{row.currentScore ?? "—"}</td>
                <td className="py-3 pr-3 text-sm bj-tnum">
                  {row.gain === null ? <span className="text-[#5E4D40]">—</span> :
                    <span className={`font-heading text-lg ${row.gain > 0 ? "text-[#14281E]" : row.gain < 0 ? "text-[#5C1A24]" : "text-[#5E4D40]"}`}>
                      {row.gain > 0 ? "+" : ""}{row.gain}
                    </span>
                  }
                </td>
                <td className="py-3 pr-3 text-[#6E5022] text-sm bj-tnum">{row.round !== null ? `Round ${row.round}` : "—"}</td>
                <td className="py-3 pr-3 text-[#2A1C15] text-sm bj-tnum">{row.removed ?? "—"}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm bj-tnum">{fmtDate(row.lastUpdated)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default CreditPipelinePage;
